/**
 * Internal helper that recursively sorts array properties of an object by the `name` field.
 *
 * Non-object and null inputs are returned as-is. Arrays of objects that contain a
 * `name` key are sorted using `localeCompare`. Other arrays and nested objects are
 * traversed recursively.
 *
 * @param obj The object (or null) to process.
 * @returns A new object with sorted array properties, or null if input is null.
 */
function deepSortArrayPropertiesSafe<T extends { name?: string }>(
  obj: T | null
): T | null {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  const result = { ...obj };

  for (const [key, value] of Object.entries(obj)) {
    if (Array.isArray(value)) {
      if (
        value.length > 0 &&
        typeof value[0] === 'object' &&
        'name' in value[0]
      ) {
        // @ts-expect-error - We are asserting that value is an array of objects with a name property
        result[key] = [...(value as { name?: string }[])]
          .sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0)
          .map((item) => deepSortArrayPropertiesSafe(item));
      } else {
        // @ts-expect-error - We are asserting that value is an array of objects with a name property
        result[key] = value.map((item: { name?: string }) =>
          deepSortArrayPropertiesSafe(item)
        );
      }
    } else if (typeof value === 'object') {
      // @ts-expect-error - We are asserting that value is an array of objects with a name property
      result[key] = deepSortArrayPropertiesSafe(value);
    }
  }

  return result;
}

/**
 * Recursively sort array properties containing objects with a `name` field.
 *
 * Creates a shallow copy of the input and sorts arrays of objects in-place by
 * their `name` using `localeCompare`. Other arrays and nested objects are traversed
 * recursively.
 *
 * @template T Object type that may contain nested arrays with `{ name?: string }` items.
 * @param obj The object to process.
 * @returns A new object with array properties sorted by `name` where applicable.
 * @example
 * const sorted = deepSortArrayProperties({ items: [{ name: 'b' }, { name: 'a' }] });
 * // sorted.items => [{ name: 'a' }, { name: 'b' }]
 */
export function deepSortArrayProperties<T extends { name?: string }>(
  obj: T
): T {
  return deepSortArrayPropertiesSafe(obj) ?? obj;
}
